import React, { useState } from 'react';
import DifficultyGauge from '../DifficultyGauge';
import { PartnershipCell, PartnershipMatrix } from './types';
import { CHROME } from './dataViz';

interface Props {
  matrix: PartnershipMatrix;
}

const CELL_SIZE = 44;
const LABEL_WIDTH = 110;
// Rightmost column's label extends up-right past the last cell; give it room.
const RIGHT_PAD = 60;

interface HoverState {
  cell: PartnershipCell;
  rowName: string;
  colName: string;
  isSingles: boolean;
  x: number;
  y: number;
}

const colorFor = (winPct: number): string => {
  // 0% red (hsl 0 70 55) → 50% amber (hsl 45 80 55) → 100% green (hsl 140 60 45)
  const hue = winPct <= 50 ? (winPct / 50) * 45 : 45 + ((winPct - 50) / 50) * (140 - 45);
  const sat = winPct <= 50 ? 70 + (winPct / 50) * 10 : 80 - ((winPct - 50) / 50) * 20;
  const light = 55 - Math.abs(winPct - 50) * 0.1;
  return `hsl(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${light.toFixed(0)}%)`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const PartnershipHeatmap: React.FC<Props> = ({ matrix }) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const players = matrix.players;
  const N = players.length;

  if (N === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No singles or doubles matches recorded yet.
      </p>
    );
  }

  const cellByPair = new Map<string, PartnershipCell>();
  for (const cell of matrix.cells) {
    const key = [cell.a_id, cell.b_id].sort((a, b) => a - b).join('-');
    cellByPair.set(key, cell);
  }

  // Aggregate each player's overall record by summing the cells they appear in.
  // Dedupe IDs so singles cells (where a_id === b_id) aren't double-counted.
  const playerOverall = new Map<number, { wins: number; matches: number }>();
  for (const cell of matrix.cells) {
    const ids = cell.a_id === cell.b_id ? [cell.a_id] : [cell.a_id, cell.b_id];
    for (const id of ids) {
      const cur = playerOverall.get(id) ?? { wins: 0, matches: 0 };
      cur.wins += cell.wins;
      cur.matches += cell.matches;
      playerOverall.set(id, cur);
    }
  }
  const fmtPlayerStats = (id: number): string | null => {
    const s = playerOverall.get(id);
    if (!s || s.matches === 0) return null;
    const pct = Math.round((s.wins / s.matches) * 100);
    return `${pct}% · ${s.wins}-${s.matches - s.wins}`;
  };

  const rowPlayers = players;
  const colPlayers = players;
  const width = LABEL_WIDTH + N * CELL_SIZE + RIGHT_PAD;
  const height = LABEL_WIDTH + N * CELL_SIZE;
  // Perpendicular offset (down-right of the rotated name) for the stats line.
  // For -40° rotation, perpendicular = (sin(40°), cos(40°)) * 12px.
  const STATS_OFFSET_X = 12 * Math.sin((40 * Math.PI) / 180);
  const STATS_OFFSET_Y = 12 * Math.cos((40 * Math.PI) / 180);

  return (
    <div className="relative overflow-x-auto" data-testid="partnership-heatmap">
      <svg width={width} height={height} role="img" aria-label="Partnership win-rate heatmap">
        {colPlayers.map((p, col) => {
          const centerX = LABEL_WIDTH + col * CELL_SIZE + CELL_SIZE / 2;
          const nameAnchorY = LABEL_WIDTH - 20;
          const stats = fmtPlayerStats(p.user_id);
          const statsX = centerX + STATS_OFFSET_X;
          const statsY = nameAnchorY + STATS_OFFSET_Y;
          return (
            <g key={`col-${p.user_id}`}>
              <text
                x={centerX}
                y={nameAnchorY}
                transform={`rotate(-40 ${centerX} ${nameAnchorY})`}
                textAnchor="start"
                fontSize={11}
                className="fill-gray-700 dark:fill-gray-200"
              >
                {p.display_name}
              </text>
              {stats && (
                <text
                  x={statsX}
                  y={statsY}
                  transform={`rotate(-40 ${statsX} ${statsY})`}
                  textAnchor="start"
                  fontSize={9}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {stats}
                </text>
              )}
            </g>
          );
        })}
        {rowPlayers.map((p, row) => {
          const centerY = LABEL_WIDTH + row * CELL_SIZE + CELL_SIZE / 2;
          const stats = fmtPlayerStats(p.user_id);
          return (
            <g key={`row-${p.user_id}`}>
              <text
                x={LABEL_WIDTH - 6}
                y={centerY - 2}
                textAnchor="end"
                fontSize={11}
                className="fill-gray-700 dark:fill-gray-200"
              >
                {p.display_name}
              </text>
              {stats && (
                <text
                  x={LABEL_WIDTH - 6}
                  y={centerY + 11}
                  textAnchor="end"
                  fontSize={9}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {stats}
                </text>
              )}
            </g>
          );
        })}
        {rowPlayers.map((rowPlayer, row) =>
          colPlayers.map((colPlayer, col) => {
            const x = LABEL_WIDTH + col * CELL_SIZE;
            const y = LABEL_WIDTH + row * CELL_SIZE;
            const isDiagonal = rowPlayer.user_id === colPlayer.user_id;
            const key = [rowPlayer.user_id, colPlayer.user_id].sort((a, b) => a - b).join('-');
            const cell = cellByPair.get(key);
            const empty = !cell || cell.matches === 0;
            if (isDiagonal && empty) {
              // Player hasn't played singles. Render inert.
              return (
                <rect
                  key={`diag-${rowPlayer.user_id}`}
                  x={x}
                  y={y}
                  width={CELL_SIZE}
                  height={CELL_SIZE}
                  className="fill-gray-100 stroke-white dark:fill-gray-800 dark:stroke-gray-900"
                  data-diagonal="true"
                />
              );
            }
            return (
              <g key={`${rowPlayer.user_id}-${colPlayer.user_id}`}>
                <rect
                  x={x}
                  y={y}
                  width={CELL_SIZE}
                  height={CELL_SIZE}
                  fill={empty ? CHROME.grid : colorFor(cell!.win_pct)}
                  stroke="#fff"
                  className={
                    empty
                      ? 'dark:fill-gray-700 dark:stroke-gray-900'
                      : 'dark:stroke-gray-900'
                  }
                  data-empty={empty ? 'true' : 'false'}
                  data-singles={isDiagonal ? 'true' : 'false'}
                  data-win-pct={cell?.win_pct ?? null}
                  onMouseEnter={
                    empty
                      ? undefined
                      : () =>
                          setHover({
                            cell: cell!,
                            rowName: rowPlayer.display_name,
                            colName: colPlayer.display_name,
                            isSingles: isDiagonal,
                            x: x + CELL_SIZE + 8,
                            y,
                          })
                  }
                  onMouseLeave={() => setHover(null)}
                />
                {!empty && (
                  <>
                    <text
                      x={x + CELL_SIZE / 2}
                      y={y + CELL_SIZE / 2 - 2}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill="#111827"
                      pointerEvents="none"
                    >
                      {Math.round(cell!.win_pct)}%
                    </text>
                    <text
                      x={x + CELL_SIZE / 2}
                      y={y + CELL_SIZE / 2 + 11}
                      textAnchor="middle"
                      fontSize={9}
                      fill="#1f2937"
                      pointerEvents="none"
                    >
                      {cell!.wins}-{cell!.matches - cell!.wins}
                    </text>
                  </>
                )}
              </g>
            );
          }),
        )}
      </svg>
      {hover && (
        <div
          role="tooltip"
          data-testid="partnership-tooltip"
          className="absolute z-20 bg-gray-900 text-white text-xs rounded shadow-lg pointer-events-none"
          style={{ left: hover.x, top: hover.y, minWidth: 240, maxWidth: 340 }}
        >
          <div className="px-3 py-2 border-b border-gray-700">
            <div className="font-semibold">
              {hover.isSingles ? `${hover.rowName} (singles)` : `${hover.rowName} + ${hover.colName}`}
            </div>
            <div className="text-gray-300 mt-0.5">
              {hover.cell.wins}-{hover.cell.matches - hover.cell.wins} (
              {Math.round(hover.cell.win_pct)}%)
            </div>
          </div>
          <div className="px-3 py-2 max-h-64 overflow-y-auto space-y-1">
            {hover.cell.match_details.map((m) => (
              <div
                key={`${m.match_id}-${m.position_number}`}
                className="flex items-baseline gap-2"
              >
                <span
                  className={`font-bold w-4 ${m.won ? 'text-green-400' : 'text-red-400'}`}
                >
                  {m.won ? 'W' : 'L'}
                </span>
                <span className="text-gray-300 w-12">{fmtDate(m.date)}</span>
                <span className="text-gray-400 text-[10px] w-6">L{m.position_number}</span>
                <span className="flex-1 truncate text-gray-300">vs {m.opponent_name}</span>
                <span className="font-mono text-[11px]">{m.score ?? '—'}</span>
                {m.team_avg_rating != null && m.opponent_avg_rating != null && (
                  <span className="self-center shrink-0" data-testid="partnership-match-difficulty">
                    <DifficultyGauge
                      teamAvg={m.team_avg_rating}
                      opponentAvg={m.opponent_avg_rating}
                      size="sm"
                      showLabel={false}
                      svgClassName="text-gray-100"
                    />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnershipHeatmap;
